import React, {useState, useEffect} from 'react';
import Paper from '@material-ui/core/Paper';
import theme from '../../../../theme';
import {
    getLocalStorageItem,
    setLocalStorageItem,
} from '../../../../utils/localStorage';
import {UserLayerDispatcher} from '../../../../hooks/useLayers';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import AddMaps from './AddMaps';
import DownloadApp from './DownloadApp';
import ControlsExplained from './ControlsExplained';
import LayersExplained from './LayersExplained';
import WelcomePage from './WelcomePage';
import ActivitySelection from './ActivitySelection';
import trackAnalytics from '../../../../utils/gaia/trackAnalytics';

export interface OnboardingPopupProps
    extends React.HTMLAttributes<HTMLDivElement> {
    userLayerDispatcher: UserLayerDispatcher;
    layerTypes: Record<string, LayerType> | undefined;
    user: User;
    userLayers: UserLayer[] | undefined;
    onSidebarChange: SetState<SidebarState>;
}

export const onboardingLocalStorageKey = 'enter_new_user_onboarding';

export default function OnboardingPopup({
    userLayerDispatcher,
    layerTypes,
    user,
    userLayers,
    onSidebarChange,
}: OnboardingPopupProps) {
    const isMobile = useMediaQuery('(max-width:750px)');
    const isSmallScreen = useMediaQuery('(max-width:1100px)');
    const [isNewUser, setNewUser] = useState(
        getLocalStorageItem(onboardingLocalStorageKey, false)
    );
    const [stepCount, setStepCount] = useState(0);
    const [activitiesList, setActivitiesList] = useState<string[]>([]);
    const [subCatList, setSubCatList] = useState<string[]>([]);

    // ensure existing users who login via register page do not see onboarding
    const timeSinceRegistered = new Date().getTime() - user.dateJoined;
    const isNewRegister = timeSinceRegistered < 86400000; //user registered less than 24 hours ago

    // url param to view pop-up
    const isOnboardingVisible =
        window && window.location.href.includes('new-onboarding');

    // check if user is viewing the map on a datasummary page
    const isViewingMainMap = window && window.location.href.includes('/map/');
    useEffect(() => {
        // if url param is present, show pop-up
        if (isOnboardingVisible) {
            setNewUser(true);
        }
    }, [isOnboardingVisible]);

    function onExitOnboarding() {
        setLocalStorageItem(onboardingLocalStorageKey, false);
        setNewUser(false);
    }

    if (!isNewUser || isMobile || !isViewingMainMap || !isNewRegister)
        return null;
    return (
        <div
            style={{
                display: 'flex',
                position: 'absolute',
                top: 0,
                transform: 'translate(-50%)',
                pointerEvents: 'none',
                zIndex: 4,
                ...(isSmallScreen ? {left: 550} : {left: '50%'}),
            }}
        >
            <Paper
                elevation={3}
                style={{
                    borderRadius: '4px',
                    padding: theme.spacing(3),
                    margin: theme.spacing(1),
                    position: 'relative',
                    pointerEvents: 'all',
                    textAlign: 'center',
                    width: 350,
                }}
            >
                <Typography
                    variant='body1'
                    style={{
                        position: 'absolute',
                        top: theme.spacing(2),
                        left: theme.spacing(3),
                        fontWeight: 500,
                    }}
                >
                    {stepCount + 1} of 6
                </Typography>
                <Button
                    style={{
                        fontSize: 14,
                        position: 'absolute',
                        top: theme.spacing(1),
                        right: 0,
                    }}
                    onClick={() => {
                        onExitOnboarding();
                        trackAnalytics('Web Onboarding', {
                            label: 'skip',
                            skip_step: stepCount,
                        });
                    }}
                >
                    Skip
                </Button>
                {stepCount === 0 && <WelcomePage setStepCount={setStepCount} />}
                {stepCount === 1 && (
                    <ActivitySelection
                        activitiesList={activitiesList}
                        setActivitiesList={setActivitiesList}
                        subCatList={subCatList}
                        setSubCatList={setSubCatList}
                        setStepCount={setStepCount}
                    />
                )}
                {stepCount === 2 && (
                    <AddMaps
                        activitiesList={activitiesList}
                        userLayerDispatcher={userLayerDispatcher}
                        layerTypes={layerTypes}
                        user={user}
                        userLayers={userLayers}
                        setStepCount={setStepCount}
                        onSidebarChange={onSidebarChange}
                    />
                )}
                {stepCount === 3 && (
                    <LayersExplained
                        setStepCount={setStepCount}
                        onSidebarChange={onSidebarChange}
                    />
                )}
                {stepCount === 4 && (
                    <ControlsExplained setStepCount={setStepCount} />
                )}
                {stepCount === 5 && (
                    <DownloadApp
                        setStepCount={setStepCount}
                        user={user}
                        onExitOnboarding={onExitOnboarding}
                    />
                )}
            </Paper>
        </div>
    );
}
